/// <reference types="vite-plugin-svgr/client" />
import { type Message, VisuallyHidden, colors, focusVisible, strings, text } from "@quickpoint.me/shared";
import type { QpFile } from "@quickpoint.me/shared/src/gql/generated/schema";
import { useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import Countdown from "../../icons/countdown.svg?react";

import Trash from "../../icons/trash.svg?react";
import { spinAnimation } from "../../lib/animations";
import { HostId, UnprocessedFilesPresent } from "../../state/uploaderState";
import { StyledUploading } from "../uploadFiles/UploadFileView";

const StyledDetails = styled.div`
    position: absolute;
    inset: 0;
    /* QuickPoint chrome surface: lavender-white, opaque so teal alt text keeps
       AAA (#14505F is 7.8:1 on this tint). Hairline frame instead of a shadow. */
    background-color: ${(props) => props.theme.navigationBackGround};
    box-shadow: inset 0 0 0 1px ${(props) => props.theme.navigationFrame};
    display: grid;
    font-family: ${(props) => props.theme.font};
    grid-template-rows: auto 1fr auto;
    align-items: center;
    justify-items: center;
    gap: 0.25rem;
    padding: 0.4rem;
    overflow: hidden;

    .user-defined-name {
        color: ${colors.textSafe.grey};
        font-weight: 500;
        letter-spacing: 0.01em;
        align-self: end;
    }
    .user-alt-text {
        color: ${colors.teal};
        font-style: italic;
        align-self: start;
    }

    /* long file names / alt text wrap and clamp inside the narrow tile,
       never pushing the overlay out of its bounds */
    .user-defined-name,
    .user-alt-text {
        max-width: 100%;
        overflow: hidden;
        overflow-wrap: anywhere;
        text-align: center;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        font-size: 0.8rem;
        line-height: 1.3;
    }
`;

const StyledTrashButton = styled.button.attrs({ type: "button" })`
    background: none;
    border: none;
    padding: 0.2rem;
    min-width: 44px;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    justify-self: end;
    cursor: pointer;
    border-radius: 6px;
    color: ${colors.textSafe.grey};
    transition: background-color 120ms ease, color 120ms ease;

    /* the destructive action reads red on hover/focus, per the app's controls */
    &:hover,
    &:focus-visible {
        background-color: ${(props) => props.theme.navigationFrame};
        color: ${colors.textSafe.red};
    }
    svg { width: 1.1rem; height: 1.1rem; }

    ${focusVisible}
`;

const StyledFile = styled.div<{ $dim: boolean; $imageUrl: string }>`
    border: ${colors.white} .25rem solid;
    aspect-ratio: 1/1;
    font-family: ${(props) => props.theme.font};
    background-image: ${(props) => (props.$imageUrl ? `url(${props.$imageUrl})` : "none")};
    background-size: cover;
    background-position: center;
    opacity: ${(props) => (props.$dim ? "0" : "1")};
    position: relative;
    border-radius: 1svw;
    /* clip the hover overlay to the rounded corners and keep everything
       inside the tile — this is what stopped the rollover blowing its margins */
    overflow: hidden;

    /* details appear on hover AND keyboard focus (WCAG 2.1.1);
       opacity, not visibility, so the trash button stays tabbable */
    .file-details {
        opacity: 0;
        pointer-events: none;
    }
    &:hover .file-details,
    &:focus-within .file-details {
        opacity: 1;
        pointer-events: auto;
    }

    .countdown {
        animation: ${spinAnimation};
        height: 50%;
        width: 50%;

    }

    .user-alt-text {
        color: ${colors.teal};
        font-style: italic;

    }
    .file-view-image-preview {
        width: 6rem;
        height: 6rem;
        object-fit: contain;
    }
`;

function ImageFileView({ file }: { file: QpFile }) {
	const dimFile = useRecoilValue(UnprocessedFilesPresent); // make the uploaded files appear lighter when new files are being uploaded.
	const [showLoader, setShowLoader] = useState(true);
	const toAuthorBC = useMemo(
		() => new BroadcastChannel(strings.broadcastChannel.author),
		[],
	);
	const [deleting, setDeleting] = useState(false);
	const [imgError, setImageError] = useState(false);
	const hostId = useRecoilValue(HostId);

	const trashImage = () => {
		setDeleting(true);
		const message: Message = {
			hostId,
			type: "fileUploader",
			action: strings.messages.deleteImage,
			payload: file.id,
		};
		toAuthorBC.postMessage(message);
	};

	useEffect(() => {
		const toUploaderBC = new BroadcastChannel(
			strings.broadcastChannel.toUploader,
		);

		toUploaderBC.onmessage = (e: MessageEvent) => {
			const data: Message = e.data;
			if (
				data?.action &&
				(data.action as any) === strings.messages.deleteImageFailed &&
				data.payload === file.id
			) {
				setDeleting(false);
			}
		};

		return () => {
			toUploaderBC.close();
		};
	}, [file.id]);

	useEffect(() => {
		const preloaderImg = document.createElement("img");
		preloaderImg.src = file.url;
		preloaderImg.onload = () => setShowLoader(false);
		if (preloaderImg.complete) {
			setShowLoader(false);
		}

		preloaderImg.onerror = () => {
			setImageError(true);
		};
	}, [file.url]);

	const trashLabel = `${text.controls.trashImage}: ${file.userDefinedName}`;

	if (imgError) {
		// StyledDetails is an absolute overlay (inset: 0) — it must sit inside a
		// StyledFile tile or it fills the whole modal instead of one grid cell.
		return (
			<StyledFile
				$dim={dimFile}
				$imageUrl=""
				data-testid={`styled-file-${file.userDefinedName}`}
				className="file file-grid"
			>
				<StyledDetails>
					<StyledTrashButton
						onClick={() => trashImage()}
						aria-label={trashLabel}
						className="file-control trash-image"
					>
						<Trash aria-hidden="true" focusable="false" />
					</StyledTrashButton>
					<span className="user-defined-name">{file.userDefinedName}</span>
					<span className="user-alt-text alt-text">{file.altText}</span>
					<span style={{ color: colors.textSafe.red, fontSize: ".8rem" }}>
						{text.fileUploader.fileMissing}
					</span>
				</StyledDetails>
				{deleting && (
					<StyledUploading>
						<Countdown className="countdown" />
					</StyledUploading>
				)}
			</StyledFile>
		);
	}

	return (
		<StyledFile
			$dim={dimFile}
			data-testid={`styled-file-${file.userDefinedName}`}
			$imageUrl={file.url}
			className="file file-grid file-drop-zone"
		>
			<VisuallyHidden>{`${file.userDefinedName}: ${file.altText}`}</VisuallyHidden>
			{showLoader && (
				<div className="loader">
					<Countdown className="countdown" />
				</div>
			)}
			<StyledDetails className="file-details">
				<StyledTrashButton
					onClick={() => trashImage()}
					aria-label={trashLabel}
					className="file-control trash-image"
					data-testid="delete-file-button"
				>
					<Trash aria-hidden="true" focusable="false" />
				</StyledTrashButton>
				<span className="user-defined-name">{file.userDefinedName}</span>

				<span className="user-alt-text alt-text">{file.altText}</span>
			</StyledDetails>
			{deleting && (
				<StyledUploading>
					<Countdown className="countdown" />
				</StyledUploading>
			)}
		</StyledFile>
	);
}

export default ImageFileView;
